import { useCallback } from 'react'
import { useAppStore } from '../store/appStore'
import { fetchWikiPOIs } from '../services/api/wikipedia'
import { fetchOSMOverpass } from '../services/api/osm'
import { fetchWikidataAroundNature } from '../services/api/wikidata'
import { fetchGooglePlaces } from '../services/api/googlePlaces'
import { now } from '../utils/geo'
import { wikiDomainFor, titleLooksUninteresting } from '../utils/text'
import { haversineMeters, bearingDeg, clampAngleDeltaDeg } from '../utils/geo'
import type { POICandidate, Position } from '../types'

/**
 * Rank candidates for a specific location
 */
function rankCandidatesForLocation(
  candidates: POICandidate[],
  location: Position
): POICandidate[] {
  const withGeo = candidates
    .filter((c) => !titleLooksUninteresting(c.title))
    .map((c) => {
      const dist = haversineMeters(location.lat, location.lon, c.lat, c.lon)
      const b = bearingDeg(location.lat, location.lon, c.lat, c.lon)
      const deltaSigned = clampAngleDeltaDeg(b - (location.heading ?? 0))
      const deltaAbs = Math.abs(deltaSigned)
      return { ...c, dist, bearing: b, deltaSigned, deltaAbs }
    })

  // Score candidates (lower is better)
  withGeo.sort((a, b) => {
    const scoreA = (a.dist || 0) + (a.deltaAbs ?? 0) * 15
    const scoreB = (b.dist || 0) + (b.deltaAbs ?? 0) * 15
    return scoreA - scoreB
  })

  return withGeo
}

/**
 * Hook to detect POIs at a specific location (for pinned locations)
 */
export function usePOIDetectionAtLocation() {
  const store = useAppStore()

  const detectPOIsAtLocation = useCallback(
    async (lat: number, lon: number) => {
      const radius = store.settings.radius
      const lang = store.language
      const candidates: POICandidate[] = []

      // Set loading state
      store.setIsFetchingPOIs(true)

      try {
        // Fetch from all sources in parallel
        const [wikiResults, osmResults, wdnResults, googleResults] = await Promise.allSettled([
          // Wikipedia
          (async () => {
            const domains = [wikiDomainFor(lang)]
            if (lang !== 'en') domains.push('en.wikipedia.org')
            for (const dom of domains) {
              try {
                const hits = await fetchWikiPOIs(dom, lat, lon, radius)
                return hits.map((h) => ({
                  source: 'wiki' as const,
                  key: `wiki:${dom}:${h.pageid}`,
                  domain: dom,
                  pageid: h.pageid,
                  title: h.title,
                  lat: h.lat,
                  lon: h.lon,
                }))
              } catch (e) {
                return []
              }
            }
            return []
          })(),
          // OSM
          store.settings.useGoogle
            ? Promise.resolve([])
            : fetchOSMOverpass(lat, lon, radius).then((osm) =>
                osm.map((o) => ({
                  source: 'osm' as const,
                  key: `osm:${o.osmType}:${o.id}`,
                  osm: o,
                  title: o.name,
                  lat: o.lat,
                  lon: o.lon,
                }))
              ),
          // Wikidata Nature
          fetchWikidataAroundNature(lat, lon, radius, lang).then((wdn) =>
            wdn.map((w) => ({
              source: 'wdnature' as const,
              key: w.key,
              title: w.title,
              lat: w.lat,
              lon: w.lon,
            }))
          ),
          // Google Places
          store.settings.useGoogle
            ? fetchGooglePlaces(lat, lon, radius).then((places) =>
                places.map((g) => ({
                  source: 'gplaces' as const,
                  key: `google:${g.id}`,
                  title: g.name,
                  lat: g.lat,
                  lon: g.lon,
                  rating: g.rating,
                  userRatings: g.userRatingsTotal,
                }))
              )
            : Promise.resolve([]),
        ])

        // Collect all candidates
        if (wikiResults.status === 'fulfilled') {
          candidates.push(...wikiResults.value)
        }
        if (osmResults.status === 'fulfilled') {
          candidates.push(...osmResults.value)
        }
        if (wdnResults.status === 'fulfilled') {
          candidates.push(...wdnResults.value)
        }
        if (googleResults.status === 'fulfilled') {
          candidates.push(...googleResults.value)
        }

        // Rank candidates
        const location: Position = { lat, lon, t: now(), heading: null }
        const ranked = rankCandidatesForLocation(candidates, location)

        // Update nearby POIs
        store.setNearbyPOIs(ranked.slice(0, 10))

        return ranked
      } catch (error) {
        console.warn('Error detecting POIs at location:', error)
        return []
      } finally {
        // Clear loading state
        store.setIsFetchingPOIs(false)
      }
    },
    [store]
  )

  return { detectPOIsAtLocation }
}
